export const NUTS_LEVEL_COLOR_MAPPING = {
  0: '#09baa5',
  1: '#81D27D',
  2: '#D18882',
  3: '#8384C9'
}

// Colour of a region with no partners at all. It is the first class of the
// legend and it is NOT produced by generateHeatmapColors: its meaning is fixed
// and must not shift with the number of classes returned by a search.
export const HEATMAP_NO_PARTNERS_COLOR = { hex: '#FFFFFF', opacity: 1 }

// Colour of the most intense class. Fixed for the same reason.
export const HEATMAP_TOP_COLOR = { hex: '#000000', opacity: 1 }

// Fill opacity of the heat map polygons. Shared with the legend swatches so the
// two can never drift apart: the colour shown in the legend is the colour the
// region is actually painted with.
export const HEATMAP_FILL_OPACITY = 0.9

// Reference palette for the intermediate classes of the heat map.
// This is the cividis colour map, which stays readable for the common forms of
// colour vision deficiency. It is ordered from LIGHT to DARK on purpose: the
// legend reads as one uninterrupted lightness ramp,
// white (no partners) -> yellow -> ... -> dark blue -> black (most intense).
// Reversing this array would break that ramp, not just recolour it.
export const BASE_HEATMAP_PALETTE = [
  { hex: '#FFEA46', opacity: 0.5 },  // lightest: least intense of the intermediate classes
  { hex: '#FDCD9A', opacity: 0.55 }, // light orange
  { hex: '#D1AD7F', opacity: 0.6 },  // beige
  { hex: '#A58F6F', opacity: 0.65 }, // light brown
  { hex: '#7C7366', opacity: 0.7 },  // gray-brown (around the mean)
  { hex: '#565B65', opacity: 0.75 }, // gray-blue
  { hex: '#31446B', opacity: 0.8 },  // blue-purple
  { hex: '#00366C', opacity: 0.7 }, // dark blue
  { hex: '#00204D', opacity: 0.7 }   // darkest: most intense of the intermediate classes
]

function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16)
  ]
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('')
}

function interpolateColor(color1, color2, t) {
  const [r1, g1, b1] = hexToRgb(color1)
  const [r2, g2, b2] = hexToRgb(color2)
  return rgbToHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t)
}

/**
 * Blends a foreground color with a background color at a given opacity
 * @param {string} fgColor - Foreground color in hex format (e.g., '#RRGGBB')
 * @param {string} bgColor - Background color in hex format (e.g., '#RRGGBB')
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} Blended color in hex format
 */
function blendColors(fgColor, bgColor, opacity) {
  const [r1, g1, b1] = hexToRgb(fgColor)
  const [r2, g2, b2] = hexToRgb(bgColor)

  // Calculate blended color: result = fg * opacity + bg * (1 - opacity)
  const r = Math.round(r1 * opacity + r2 * (1 - opacity))
  const g = Math.round(g1 * opacity + g2 * (1 - opacity))
  const b = Math.round(b1 * opacity + b2 * (1 - opacity))

  return rgbToHex(r, g, b)
}

/**
 * Blends colors with gamma correction to match browser rendering
 * Uses sRGB color space with gamma 2.2 correction for more accurate blending
 * @param {string} fgColor - Foreground color in hex format (e.g., '#RRGGBB')
 * @param {string} bgColor - Background color in hex format (e.g., '#RRGGBB')
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} Blended color in hex format
 */
function blendColorsGammaCorrected(fgColor, bgColor, opacity) {
  const [r1, g1, b1] = hexToRgb(fgColor)
  const [r2, g2, b2] = hexToRgb(bgColor)

  // Convert to linear space (inverse gamma 2.2)
  const linearR1 = Math.pow(r1/255, 2.2)
  const linearG1 = Math.pow(g1/255, 2.2)
  const linearB1 = Math.pow(b1/255, 2.2)
  const linearR2 = Math.pow(r2/255, 2.2)
  const linearG2 = Math.pow(g2/255, 2.2)
  const linearB2 = Math.pow(b2/255, 2.2)

  // Blend in linear space
  const blendedR = linearR1 * opacity + linearR2 * (1 - opacity)
  const blendedG = linearG1 * opacity + linearG2 * (1 - opacity)
  const blendedB = linearB1 * opacity + linearB2 * (1 - opacity)

  // Convert back to sRGB space with gamma correction
  const finalR = Math.round(Math.pow(blendedR, 1/2.2) * 255)
  const finalG = Math.round(Math.pow(blendedG, 1/2.2) * 255)
  const finalB = Math.round(Math.pow(blendedB, 1/2.2) * 255)

  return rgbToHex(finalR, finalG, finalB)
}

/**
 * Builds the colours of the heat map classes actually present in a search.
 *
 * The backend returns only the non-empty classes, densely renumbered from 0, so
 * `count` varies between searches. The scale is anchored at the top: the most
 * intense class present is always HEATMAP_TOP_COLOR (black) and the remaining
 * ones are interpolated over BASE_HEATMAP_PALETTE from light to dark. Together
 * with the fixed white of HEATMAP_NO_PARTNERS_COLOR the legend always reads as
 * a single white -> black ramp, whatever the number of classes.
 *
 * @param {number} count - Number of classes returned by the search
 * @returns {Object} Object with keys 0..count-1 and hex colour values
 */

function interpolate(a, b, t) {
  return a + (b - a) * t
}

function interpolateStop(stop1, stop2, t) {
  return {
    hex: interpolateColor(stop1.hex, stop2.hex, t),
    opacity: interpolate(stop1.opacity, stop2.opacity, t)
  }
}

export function generateHeatmapColors(count) {
  if (count <= 0) return {}
  // A single class is not a ramp: black would paint the whole map. Use a middle
  // tone of the palette instead.
  if (count === 1) return { 0: BASE_HEATMAP_PALETTE[4] }

  const colors = {}
  const rampCount = count - 1 // the top class is black, the rest come from the palette

  for (let i = 0; i < rampCount; i++) {
    const t = rampCount === 1 ? 0 : i / (rampCount - 1)
    const palettePos = t * (BASE_HEATMAP_PALETTE.length - 1)
    const idx = Math.floor(palettePos)
    const frac = palettePos - idx
    if (idx >= BASE_HEATMAP_PALETTE.length - 1) {
      colors[i] = BASE_HEATMAP_PALETTE[BASE_HEATMAP_PALETTE.length - 1]
    } else {
      colors[i] = interpolateStop(BASE_HEATMAP_PALETTE[idx], BASE_HEATMAP_PALETTE[idx + 1], frac)
    }
  }

  colors[count - 1] = HEATMAP_TOP_COLOR

  return colors
}
/**
 * Generates pre-blended heatmap colors that will match the legend when rendered with opacity
 * @param {number} count - Number of colors to generate
 * @param {string} [bgColor='#ffffff'] - Background color in hex format (the map land colour)
 * @param {number} [opacity=0.5] - Opacity value between 0 and 1
 * @param {boolean} [useGammaCorrection=false] - Use gamma-corrected blending for more accurate browser matching
 * @returns {Object} Object with keys 1..count and hex color values
 */
export function generatePreblendedHeatmapColors(count, bgColor = '#ffffff', useGammaCorrection = false) {
  if (count <= 0) return {}
  if (count === 1) {
    const blendFunction = useGammaCorrection ? blendColorsGammaCorrected : blendColors
    const stop = BASE_HEATMAP_PALETTE[4]
    return { 0: blendFunction(stop.hex, bgColor, stop.opacity) }
  }

  const initialColors = generateHeatmapColors(count)
  const colors = {}
  const blendFunction = useGammaCorrection ? blendColorsGammaCorrected : blendColors

  for (let i = 0; i < count; i++) {
    colors[i] = blendFunction(initialColors[i].hex, bgColor, initialColors[i].opacity)
  }

  console.table(
    Object.keys(colors).map((key) => ({
      index: key,
      original: initialColors[key].hex,
      opacity: initialColors[key].opacity,
      preblended: colors[key],
      bgColor
    }))
  )

  return colors
}

export const PARTNER_TYPE_COLOR_MAPPING = {
  lead: '#ff6d10',
  partner: '#1c7b41'
}

export const customMapStyles = [
  {
    // Land is white so the heat map ramp (white -> black) is read against a
    // neutral base and a region with no partners blends into the land.
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161'
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffeb3b'
      },
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#797979'
      },
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#bdbdbd'
      },
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#bdbdbd'
      }
    ]
  },
  {
    featureType: 'landscape.natural.landcover',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'landscape.natural.landcover',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e'
      }
    ]
  },
  {
    featureType: 'road',
    stylers: [
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e'
      }
    ]
  },
  {
    featureType: 'transit',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    // Google Maps styling exposes a single 'water' feature: sea, lakes and
    // rivers cannot be given different colours here. Grey is applied to all of
    // them, which gives the requested grey sea; inland water inside a mapped
    // region is hidden anyway, since the polygons are now fully opaque.
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#c9c9c9'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#c9c9c9'
      },
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e'
      }
    ]
  }
]

export const SHOW_FILTER_SEARCH_LIMIT = 7

export const SELECTED_FILTER_MAX_LINES = 2

export const SearchableOptions = Object.freeze({
  PROJECTS: 'projects',
  PROGRAMMES: 'programmes',
  PARTNERS: 'partners',
  COUNTRIES_AND_REGIONS: 'countries_and_regions'
})

// RESULTS_PER_PAGE is also set in the backend so don't change this value without doing the change also in the backend
export const PROJECTS_RESULTS_PER_PAGE = process.env.REACT_APP_PROJECTS_RESULTS_PER_PAGE
export const PROGRAMMES_RESULTS_PER_PAGE = process.env.REACT_APP_PROGRAMMES_RESULTS_PER_PAGE
export const CARPARTNERS_RESULTS_PER_PAGE = process.env.REACT_APP_CARPARTNERS_RESULTS_PER_PAGE
export const PARTNERS_RESULTS_PER_PAGE = process.env.REACT_APP_PARTNERS_RESULTS_PER_PAGE

export const DEFAULT_PROJECT_START_YEAR_MIN = 2000
export const DEFAULT_PROJECT_START_YEAR_MAX = 2025
export const DEFAULT_PROJECT_END_YEAR_MIN = 2000
export const DEFAULT_PROJECT_END_YEAR_MAX = 2025
export const INCLUDED_VALUE = 'included'

export const PermissionStatusStates = Object.freeze({
  DENIED: 'denied',
  GRANTED: 'granted',
  PROMPT: 'prompt'
})

export const LIMIT_WHEN_CLUSTER_CLICK_WILL_ZOOM = 100

export const OTHER_EUSDR_RELATED = 'Other (EUSDR related)'
export const OTHER_EUSBSR_RELATED = 'Other  (EUSBSR related)'
export const ENPI_ENI_CROSS_BORDER = 'ENPI/ENI cross-border'
export const INTERREG_IPA_CROSS_BORDER = 'Interreg-IPA cross-border'
export const IPA_IPA_CROSS_BORDER_COOPERATION = 'IPA-IPA cross-border'

export const PERIOD_2000_2006 = '2000-2006'

export const MAX_SEARCH_WORDS = 20

export const IS_SEARCH_REPRESANTIVENESS_DISABLED = process.env.REACT_APP_IS_SEARCH_REPRESANTIVENESS_DISABLED.toLowerCase() === "true";
